import { useApolloClient } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Progress,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import { FacilitySummary, OnMappingMatched } from './AliasMatchingTable';
import { AutoMatchesByFacility } from './useAutoMatchSuggestions';
import { useBulkAutoMatch } from './useBulkAutoMatch';
import { GetLimitedPartnerMappingsForCreditFacilityDocumentGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';

// how often the facility counts (header badges, status cards) refresh while a
// bulk run is in flight
const REFETCH_FACILITIES_DURING_RUN_MS = 2000;

/**
 * Entry point for auto-matching across facilities. Off: a button that loads
 * the org-wide suggestions and narrows the list to facilities ready to
 * auto-match. On: a banner with the total and a Match All over those
 * facilities (within the current search), showing progress while it runs.
 */
export const AutoMatchAcrossFacilities = ({
  facilities,
  searchQuery,
  autoMatchesByFacility,
  isLoadingSuggestions,
  isReadyOnly,
  onShowReady,
  onShowAll,
  onMappingMatched,
  refetchFacilities,
}: {
  facilities: FacilitySummary[];
  searchQuery: string;
  autoMatchesByFacility: AutoMatchesByFacility | null;
  isLoadingSuggestions: boolean;
  isReadyOnly: boolean;
  onShowReady: () => Promise<void>;
  onShowAll: () => void;
  onMappingMatched: OnMappingMatched;
  refetchFacilities: () => void;
}): React.ReactElement => {
  const apolloClient = useApolloClient();
  const { runBulkAutoMatch, progress } = useBulkAutoMatch();

  const refetchFacilitiesThrottled = useMemo(
    () => _.throttle(refetchFacilities, REFETCH_FACILITIES_DURING_RUN_MS),
    [refetchFacilities],
  );

  // Outstanding matches per listed facility. This recomputes after every
  // match of a run (tens of thousands for a large org), so it only sums
  // per-facility counts; the flat match list is built once, on click.
  const outstandingByFacility = useMemo(
    () =>
      facilities.flatMap((facility) => {
        const matches = autoMatchesByFacility?.get(facility.creditFacilityId);
        return matches && matches.length > 0 ? [matches] : [];
      }),
    [facilities, autoMatchesByFacility],
  );
  const matchCount = _.sumBy(outstandingByFacility, (m) => m.length);
  const facilityCount = outstandingByFacility.length;

  const handleMatchAll = async (): Promise<void> => {
    // in list order, so facilities finish roughly one after another
    await runBulkAutoMatch(outstandingByFacility.flat(), (match) => {
      onMappingMatched(match);
      refetchFacilitiesThrottled();
    });
    refetchFacilitiesThrottled.cancel();
    refetchFacilities();
    // refresh the mappings of any facility accordion that is open
    await apolloClient.refetchQueries({
      include: [GetLimitedPartnerMappingsForCreditFacilityDocumentGQLType],
    });
  };

  if (!isReadyOnly) {
    return (
      <Flex justifyContent="flex-end" mt="1rem">
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          isLoading={isLoadingSuggestions}
          onClick={onShowReady}
        >
          Show facilities ready to auto-match
        </Button>
      </Flex>
    );
  }

  const trimmedQuery = searchQuery.trim();
  const inSearch = trimmedQuery ? ` matching "${trimmedQuery}"` : '';

  const describeStatus = (): string => {
    if (progress) {
      return `Matching ${progress.settled} of ${progress.total} aliases…`;
    }
    if (matchCount === 0) {
      return `No aliases${inSearch} are ready to auto-match.`;
    }
    const aliases = matchCount === 1 ? 'alias is' : 'aliases are';
    const facilitiesLabel = facilityCount === 1 ? 'facility' : 'facilities';
    return `${matchCount} ${aliases} ready to auto-match across ${facilityCount} ${facilitiesLabel}${inSearch}. They exactly match existing LP names.`;
  };

  return (
    <Alert status="info" variant="subtle" borderRadius="6px" mt="1rem">
      <AlertIcon />
      <Box flex="1">
        <AlertDescription>{describeStatus()}</AlertDescription>
        {progress && (
          <Progress
            mt="8px"
            size="sm"
            borderRadius="4px"
            value={(progress.settled / progress.total) * 100}
          />
        )}
      </Box>
      <Flex gap="8px" ml="16px">
        {matchCount > 0 && (
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={progress !== null}
            onClick={handleMatchAll}
          >
            Match All
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          isDisabled={progress !== null}
          onClick={onShowAll}
        >
          Show all facilities
        </Button>
      </Flex>
    </Alert>
  );
};
